import React from 'react';
import { useTranslation } from 'react-i18next';

const VERDICT_STYLES = {
  compliant: 'bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-100',
  non_compliant: 'bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-100',
  undetermined: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100'
};

const STATUS_STYLES = {
  met: 'text-green-700 dark:text-green-300',
  not_met: 'text-red-700 dark:text-red-300',
  not_applicable: 'text-gray-500 dark:text-gray-400',
  undetermined: 'text-amber-700 dark:text-amber-300'
};

/**
 * Compliance results.
 *
 * Criterion labels come from the version that produced the analysis, not from the configuration as
 * it stands today — a grid edited since would otherwise relabel an old result.
 */
const ComplianceVerdict = ({ output, configParams }) => {
  const { t } = useTranslation();
  const details = output.details || {};
  const labelFor = (criterion) => criterion.label
    || (configParams?.criteria || []).find(entry => entry.key === criterion.key)?.label
    || criterion.key;

  return (
    <div className="space-y-3">
      <div className={`rounded-xl px-4 py-3 ${VERDICT_STYLES[output.verdict] || 'bg-gray-100 dark:bg-gray-800'}`}>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-lg font-semibold">{t(`verdict.${output.verdict}`, { defaultValue: output.verdict })}</span>
          {details.confidence && <span className="text-xs opacity-80">{t('confidence: {{level}}', { level: details.confidence })}</span>}
          {details.strictness && <span className="text-xs opacity-80">· {t('strictness: {{level}}', { level: details.strictness })}</span>}
        </div>
        <p className="mt-2 text-sm">{output.summary}</p>
      </div>

      {details.blocking_issues?.length > 0 && (
        <ul className="list-inside list-disc rounded-xl bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950/40 dark:text-red-100">
          {details.blocking_issues.map(issue => <li key={issue}>{issue}</li>)}
        </ul>
      )}

      <div className="space-y-2">
        {(details.criteria || []).map(criterion => (
          <div key={criterion.key} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {labelFor(criterion)}
                {criterion.blocking === false && <span className="ml-2 text-xs text-gray-400">{t('non blocking')}</span>}
              </span>
              <span className={`text-xs font-semibold uppercase ${STATUS_STYLES[criterion.status] || ''}`}>
                {t(`criterion.${criterion.status}`, { defaultValue: criterion.status })}
              </span>
            </div>
            {criterion.evidence && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{criterion.evidence}</p>}
          </div>
        ))}
      </div>

      {output.quality?.limitations?.length > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {t('Limits')} : {output.quality.limitations.join(' · ')}
        </p>
      )}
    </div>
  );
};

export default ComplianceVerdict;
